export function formatPrice(value: number) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .concat('đ')
}
// tạo hàm format phần nghìn và thêm chữ k ở cuối, ví dụ 20000 -> 20k, 1000 -> 1k
export const formatPriceWithK = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value / 1000)
    .concat('k')
}

export const getValue = <T, V>(
  obj: T,
  path: string | (keyof T)[],
  defaultValue: V
): V => {
  if (!obj || typeof obj !== 'object') return defaultValue

  const keys = Array.isArray(path)
    ? path
    : (path as string).split(/[.[\]]/).filter(Boolean)

  let result: unknown = obj
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = (result as Record<string, unknown>)[key as string]
    } else {
      return defaultValue
    }
  }

  return (result as V) ?? defaultValue
}

type KeyFunc<T> = (item: T) => string | number

export const groupBy = <T>(
  array: T[],
  keyFn: KeyFunc<T>
): Record<string, T[]> => {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item)
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    },
    {} as Record<string, T[]>
  )
}

export const hasVietnamese = (str: string): boolean => {
  return /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệđìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵ]/i.test(
    str.toLocaleLowerCase()
  )
}

export function stringToSlug(str: string): string {
  str = str.replace(/<[^>]*>/g, '')
  // Convert to lowercase and trim
  str = str.toLowerCase().trim()

  // Replace Vietnamese characters with their non-accented equivalents
  const charMap: Record<string, string> = {
    'à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ': 'a',
    'è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ': 'e',
    'ì|í|ị|ỉ|ĩ': 'i',
    'ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ': 'o',
    'ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ': 'u',
    'ỳ|ý|ỵ|ỷ|ỹ': 'y',
    đ: 'd',
  }

  // Replace accented characters
  for (const [pattern, replacement] of Object.entries(charMap)) {
    str = str.replace(new RegExp(pattern, 'g'), replacement)
  }

  // Remove special characters and convert spaces to hyphens
  str = str.replace(/[^a-z0-9-\s]/g, '')
  str = str.replace(/\s+/g, '-')

  return str
}

export function buildQuery(params: Record<string, unknown>): string {
  return new URLSearchParams(
    Object.entries(params)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([key, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  ).toString()
}

export function throttle<T extends (...args: never[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastExecuted = 0
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    const now = Date.now()

    const remaining = delay - (now - lastExecuted)
    if (remaining <= 0) {
      lastExecuted = now
      fn(...args)
    } else if (!timeout) {
      timeout = setTimeout(() => {
        lastExecuted = Date.now()
        timeout = null
        fn(...args)
      }, remaining)
    }
  }
}

/**
 * Creates an object excluding the specified properties from the given object.
 * @param obj The source object
 * @param keys The property keys to omit
 * @returns A new object without the omitted properties
 */
export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[] | K
): Omit<T, K> {
  const keysArray = Array.isArray(keys) ? keys : [keys]
  const result = { ...obj }

  for (const key of keysArray) {
    delete result[key]
  }

  return result as Omit<T, K>
}
/**
 * Formats a number with dot separators for thousands
 * @param number The number to format
 * @returns The formatted number string with dots as thousand separators, or 0 if input is falsy
 * @example
 * formatNumber(1000) // returns "1.000"
 * formatNumber(1000000) // returns "1.000.000"
 * formatNumber(0) // returns 0
 */
export const formatNumber = (number: number) => {
  if (!number) return 0
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export const normalizeVietnamese = (str: string) =>
  str
    .trim()
    .normalize('NFD') // tách dấu
    .replace(/[\u0300-\u036f]/g, '') // xóa dấu
    .replace(/đ/g, 'd') // đ -> d
    .replace(/Đ/g, 'D')
    .replace(/\s+/g, '') // xóa khoảng trắng
    .toLowerCase()

export const findValueInOptions = (
  value: string,
  options: { label: string; value: string }[]
) => {
  if (!value) return ''

  const find = options.find((option) =>
    normalizeVietnamese(option.value).includes(normalizeVietnamese(value))
  )
  return find?.value ?? ''
}

export const convertPhone = (phoneNumber: string): string => {
  // Xóa tất cả khoảng trắng, dấu gạch, dấu ngoặc, v.v.
  const cleaned = phoneNumber.replace(/[^0-9+]/g, '')

  if (cleaned.startsWith('+84')) {
    return '0' + cleaned.slice(3)
  }

  if (cleaned.startsWith('84')) {
    return '0' + cleaned.slice(2)
  }

  // Nếu đã bắt đầu bằng 0 thì giữ nguyên
  if (cleaned.startsWith('0')) {
    return cleaned
  }

  // Trường hợp khác (không hợp lệ), có thể xử lý theo cách riêng hoặc trả lại nguyên
  return cleaned
}
/**
 * Chuyển đổi số thành định dạng rút gọn với đơn vị K, M, B
 * @param num - Số cần chuyển đổi
 * @returns Chuỗi số đã được chuyển đổi
 * @example
 * convertNumber(1234) // returns "1.2K"
 * convertNumber(1234567) // returns "1.2M"
 * convertNumber(1234567890) // returns "1.2B"
 * convertNumber(99) // returns 99
 */

export function convertPhoneNumberToVietnam(number: string) {
  // Nếu không tồn tại thì return '' luôn
  if (number == '' || number == null || number == undefined) {
    return ''
  }
  // Xóa mọi ký tự không phải chữ số khỏi số đầu vào
  number = number.replace(/\D/g, '')

  // Kiểm tra xem số có bắt đầu bằng mã quốc gia Việt Nam không
  // Nếu có thì remove bỏ mã, k có thì remove số 0 ở đầu
  if (number.startsWith('84')) {
    number = number.slice(2)
  } else if (number.startsWith('+84')) {
    number = number.slice(1)
  } else if (number.startsWith('0')) {
    number = number.slice(1)
  }

  // Thêm mã quốc gia vào đầu số
  number = '+84' + number

  return number
}

export const convertNumber = (num: number): string | number => {
  const numRound = Math.round(num)

  const format = (value: number, unit: string) => {
    const fixed = value.toFixed(1)
    // Bỏ .0 nếu có
    return (fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed) + unit
  }

  if (numRound >= 1_000_000_000) return format(numRound / 1_000_000_000, 'B')
  if (numRound >= 1_000_000) return format(numRound / 1_000_000, 'M')
  if (numRound >= 1_000) return format(numRound / 1_000, 'K')
  if (numRound < 100) return num
  return numRound
}

export const maskString = (
  input: string,
  start: number,
  endOffset: number,
  maskLength?: number
): string => {
  const len = input.length
  if (len === 0 || start >= len) return input

  const end = len + endOffset
  const stars = '*'.repeat(maskLength ?? Math.floor(Math.random() * 5) + 1)

  return input.slice(0, start) + stars + input.slice(end)
}

export const normalizeSlug = (input: string): string => {
  try {
    const url = new URL(input)
    const path = url.pathname.replace(/^\/|\/$/g, '') // bỏ / đầu và cuối
    const query = url.search // giữ nguyên query string
    return `${path}${query}`
  } catch {
    // Nếu không phải URL hợp lệ, giả định là slug rồi → giữ nguyên
    return input.replace(/^\/|\/$/g, '')
  }
}

export const getSeasonFromCreatedDate = (date: Date): string => {
  const month = new Date(date).getMonth() + 1
  if ([12, 1, 2].includes(month)) return 'Winter'
  if ([3, 4, 5].includes(month)) return 'Spring'
  if ([6, 7, 8].includes(month)) return 'Summer'
  return 'Autumn'
}

export const estimateDeliveryTime = (raw: string): string => {
  return raw || '3-5 days'
}
